import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import { useUser } from "@/context/UserContext";
import { CollaborationStatus } from "@/components/shared/constants/general.const";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { downloadAnonymizedProfilePdf } from "@/utils/anonymizedProfilePdf";
import { errorNotification } from "@/shared/utils/errorNotification";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";

export const WORKSPACE_PARTNER_BOOKMARK_BTN_STYLES = {
  root: {
    backgroundColor: "#04305c",
    color: "white",
    border: "1px solid #04305c",
    "&:hover": {
      backgroundColor: "#032847",
      borderColor: "#032847",
    },
  },
};

export function useWorkspacePartnerHeader({
  company,
  collaboration,
  onCollaborationChange,
}: {
  company: any;
  collaboration: any | null;
  onCollaborationChange: (collaboration: any | null) => void;
}) {
  const {
    userDetails,
    shortlistedIds,
    addToShortlist,
    removeFromShortlist,
    removeInterestedCompany,
    refreshPendingInterestCount,
    refreshCollaborationSidebarCounts,
  } = useUser();

  const [isFavorited, setIsFavorited] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [anonymizedPdfLoading, setAnonymizedPdfLoading] = useState(false);
  const [acceptModalOpened, { open: openAcceptModal, close: closeAcceptModal }] = useDisclosure(false);
  const [declineConfirmOpened, { open: openDeclineConfirm, close: closeDeclineConfirm }] = useDisclosure(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const navigate = useNavigate();

  const companyId = company?.id as string | undefined;
  const userCompanyId = userDetails?.company?.id;

  useEffect(() => {
    if (companyId) setIsFavorited(shortlistedIds.has(companyId));
  }, [shortlistedIds, companyId]);

  const headquartersFlagEmoji = useMemo(
    () => flagEmojiFromHeadquartersCountry(company?.headquartersCountry),
    [company?.headquartersCountry]
  );

  const canIncomingReceiverDownloadAnonymizedPdf = Boolean(
    company &&
      collaboration?.status === CollaborationStatus.REQUESTED &&
      userCompanyId === collaboration.receiverCompanyId &&
      company.id === collaboration.requesterCompanyId
  );

  const refetchCollaboration = useCallback(async () => {
    if (!companyId) return;
    try {
      const next = await trpc.collaboration.getByCompanyId.query({ targetCompanyId: companyId });
      onCollaborationChange(next);
    } catch {
      onCollaborationChange(null);
    }
    refreshPendingInterestCount();
    refreshCollaborationSidebarCounts();
  }, [companyId, onCollaborationChange, refreshPendingInterestCount, refreshCollaborationSidebarCounts]);

  const handleDownloadAnonymizedProfilePdf = useCallback(async () => {
    if (!company) return;
    setAnonymizedPdfLoading(true);
    try {
      await downloadAnonymizedProfilePdf(company, collaboration);
    } catch (err) {
      errorNotification({
        title: "Could not create PDF",
        message: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setAnonymizedPdfLoading(false);
    }
  }, [company, collaboration]);

  const handleShortlistClick = async () => {
    if (!companyId) return;
    if (!isFavorited) {
      try {
        await addToShortlist(companyId);
        setIsFavorited(true);
        successNotification({ title: "Success", message: "Company added to shortlist" });
      } catch (err) {
        handleTrpcError(err);
      }
    } else {
      try {
        await removeFromShortlist(companyId);
        setIsFavorited(false);
        successNotification({ title: "Success", message: "Company removed from shortlist" });
      } catch (err) {
        handleTrpcError(err);
      }
    }
  };

  const handleAcceptConfirm = async () => {
    if (!collaboration?.id) return;
    setAcceptLoading(true);
    try {
      await trpc.collaboration.accept.mutate({ matchId: collaboration.id });
      successNotification({ title: "Success", message: "Collaboration request accepted!" });
      closeAcceptModal();
      await refetchCollaboration();
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!collaboration?.id || !companyId) return;
    setDeclineLoading(true);
    try {
      await trpc.collaboration.reject.mutate({ matchId: collaboration.id });
      closeDeclineConfirm();
      successNotification({ title: "Success", message: "Collaboration request declined." });
      removeInterestedCompany(companyId);
      refreshPendingInterestCount();
      refreshCollaborationSidebarCounts();
      navigate("/dashboard");
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setDeclineLoading(false);
    }
  };

  return {
    userCompanyId,
    isFavorited,
    headquartersFlagEmoji,
    canIncomingReceiverDownloadAnonymizedPdf,
    anonymizedPdfLoading,
    declineLoading,
    acceptModalOpened,
    declineConfirmOpened,
    acceptLoading,
    openAcceptModal,
    closeAcceptModal,
    openDeclineConfirm,
    closeDeclineConfirm,
    handleShortlistClick,
    handleDownloadAnonymizedProfilePdf,
    handleAcceptConfirm,
    handleDeclineConfirm,
  };
}
